import React, { createContext, useContext, useEffect, useState } from "react";
import { getLogo } from "src/shared/services/services";
import businessLogo from "src/assets/Images/imetanic-logo.png";
import { IS_STANDALONE } from "src/features/auth/axiosConfig";
import PrintAppFind from "src/printConfigration/PrintAppFind";

export const toBoolean = (value: any) => value === true || value === "true";

export interface UserDetails {
  name: string;
  user_name: string;
  email_id: string;
  role: string;
  organization_id: string;
  createdOn: string;
  organization_name: string;
  image_url: string;
  phone_number: string;
  country: string;
  state: string;
  business_type: string;
  profile_image_url: string;
  organization_arabic_name: string;
  business_location: string;
  background_colour: string;
  font_colour: string;
  font_family: string;
  date_of_birth: string;
  address_line_1: string;
  postal_code: string;
  companyLogo: string | null;
  printProductLimit?: string;
  companyLogoAPP?: string | null;
  permissions: any[];
  employee_name: string
}

export interface Configration {
  printType: "print:2" | "print:1" | "print:3";
  tableDefultSize: number;
  copyqty: number;
  billResetPeriod?: string;
  billNoEnabled: boolean;
  orderIdEnabled?: boolean;
  foodTypes?: string[];
  shiftTypes?: string[];
  termsAndConditionsEnglish?: string;
  termsAndConditionsArabic?: string;
  enabileTermsAndConditions?: boolean;
  contact_qr_code: string;
  location_qr_code: string;
  isProductsNotesEnabled: boolean;
  isDefaultTabsEnabled: boolean;
  deliveryFeeEnabled?: boolean;
  businessOperationEnable?: boolean;
  businessOperationType?: "Day" | "Shift";
  pdfProductLimit?: string;
  showOutstandingInPdf?: boolean;
  customBusinessName?: string;
  printProductLimit?: string;
  orderTableType?: "Table Type:1" | "Table Type:2";
  appPrinter?: boolean;
  contact_qr_codeAWS?: string;
  location_qr_codeAWS?: string;
  appPrinterEnabled?: boolean | string;
}

export interface SubscriptionInfo {
  expiry_date?: string;
  grace_end_date?: string;
  message?: string;
  status: "active" | "inactive" | "expired" | "grace_period" | "blocked";
  warning?: string | null;
  daysRemaining?: number;
}

interface UserContextProps {
  user: UserDetails | null;
  setUser: (user: UserDetails | null) => void;
  userConfigration: Configration;
  setConfigurations: (config: Configration) => void;

  subscriptionInfo: SubscriptionInfo | null;
  setSubscriptionInfo: (info: SubscriptionInfo | null) => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDetails | null>(null);
  const { NewPrinterList } = PrintAppFind();

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);

  const [userConfigration, setUserConfigration] = useState<Configration>({
    printType: "print:1",
    tableDefultSize: 10,
    copyqty: 1,
    billResetPeriod: "noreset",
    billNoEnabled: false,
    orderIdEnabled: false,
    foodTypes: ["Veg", "Non-Veg"],
    contact_qr_code: "",
    location_qr_code: "",
    termsAndConditionsEnglish: "",
    termsAndConditionsArabic: "",
    enabileTermsAndConditions: false,
    isDefaultTabsEnabled: false,
    isProductsNotesEnabled: false,
    deliveryFeeEnabled: false,
    printProductLimit: "5",
    customBusinessName: "Grocery",
    orderTableType: "Table Type:1",
    contact_qr_codeAWS: "",
    location_qr_codeAWS: "",
    appPrinterEnabled: false,
  });

  const setConfigurations = (config: Configration) => {
    if (config.contact_qr_code) {
      const base64 = config.contact_qr_code.startsWith("data:image") ? config.contact_qr_code : `data:image/png;base64,${config.contact_qr_code}`;
      config = { ...config, contact_qr_code: base64 };
    }

    if (config.location_qr_code) {
      const base64 = config.location_qr_code.startsWith("data:image") ? config.location_qr_code : `data:image/png;base64,${config.location_qr_code}`;
      config = { ...config, location_qr_code: base64 };
    }

    if (config) {
      setUserConfigration((prev) => ({ ...prev, ...config }));
    }
  };

  useEffect(() => {
    if (!user?.image_url) return;

    const imageLogo = user?.image_url;
    const standalone = IS_STANDALONE;

    if (standalone) {
      setUser((prev) => (prev ? { ...prev, companyLogo: `data:image/png;base64,${imageLogo}` } : prev));
      return;
    }

    getLogo(user.image_url)
      .then((response: any) => {
        const blob = new Blob([response.data], { type: "image/png" });
        const imageUrl = URL.createObjectURL(blob);

        setUser((prev) => (prev ? { ...prev, companyLogo: imageUrl } : prev));
      })
      .catch(() => {
        setUser((prev) => (prev ? { ...prev, companyLogo: businessLogo } : prev));
      });
  }, [user?.image_url]);

  useEffect(() => {
    const cleanBase64 = (value: string | undefined) => {
      if (!value) return "";
      return value.replace(/^data:image\/[a-zA-Z]+;base64,/, "");
    };

    const imageUrl = cleanBase64(user?.image_url);
    const qrCodeUrl = cleanBase64(userConfigration?.contact_qr_code);
    const locationQrCodeUrl = cleanBase64(userConfigration?.location_qr_code);

    if (NewPrinterList.length > 0 && toBoolean(userConfigration?.appPrinterEnabled) === true) {
      setUser((prev) =>
        prev
          ? {
            ...prev,
            companyLogoAPP: `${process.env.REACT_APP_IMAGE_PATH}/${imageUrl}`,
          }
          : prev,
      );

      setUserConfigration((prev) => ({
        ...prev,
        contact_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${qrCodeUrl}`,
        location_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${locationQrCodeUrl}`,
        appPrinter: true,
      }));
    } else if (toBoolean(userConfigration?.appPrinterEnabled) === false) {
      setUser((prev) => (prev ? { ...prev, companyLogoAPP: user?.companyLogo } : prev));
      getLogo(qrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({ ...prev, contact_qr_codeAWS: imageUrl, appPrinter: false }));
        })
        .catch((err) => console.log(err));
      getLogo(locationQrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({ ...prev, location_qr_codeAWS: imageUrl, appPrinter: false }));
        })
        .catch((err) => console.log(err));
    }
  }, [
    NewPrinterList,
    user?.image_url,
    userConfigration?.contact_qr_code,
    userConfigration?.location_qr_code,
    user?.companyLogo,
    userConfigration?.appPrinterEnabled,
  ]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userConfigration,
        setConfigurations,
        subscriptionInfo,
        setSubscriptionInfo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};
